if (!customElements.get('td-snap-dots')) {
  const SELECTORS = {
    track: '[data-td-snap-track]',
    dot: '[data-td-snap-dot]',
    prev: '[data-td-snap-prev]',
    next: '[data-td-snap-next]',
  };
  const ACTIVE_CLASS = 'td-article-products__dot--active';
  const END_TOLERANCE = 2;
  const TARGET_HOLD = 800;
  const DRAG_THRESHOLD = 6;
  const DRAGGING_CLASS = 'td-article-products__scroller--dragging';

  // Pagination dots for a native scroll-snap row: the active dot follows the
  // scroll position and a tapped dot scrolls its card into place. Optional
  // prev/next arrows (desktop carousel) move one card and disable at the ends.
  // A mouse can drag the row (touch keeps native swiping); it lands on the
  // nearest card, and a drag never counts as a click on the card under it.
  class TdSnapDots extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTrackClick = this.onTrackClick.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.drag = null;
      this.suppressClick = false;
      this.frame = null;
      this.activeIndex = 0;
      this.targetIndex = null;
      this.targetTimer = null;
    }

    connectedCallback() {
      // A reconnect (theme editor move) must not inherit a pending frame or hold timer.
      this.teardown();
      this.track = this.querySelector(SELECTORS.track);
      this.dots = [...this.querySelectorAll(SELECTORS.dot)];
      this.prev = this.querySelector(SELECTORS.prev);
      this.next = this.querySelector(SELECTORS.next);
      if (!this.track || this.dots.length === 0) return;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.prev?.addEventListener('click', this.onPrevClick);
      this.next?.addEventListener('click', this.onNextClick);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('click', this.onTrackClick, true);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.updateArrows();
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.prev?.removeEventListener('click', this.onPrevClick);
      this.next?.removeEventListener('click', this.onNextClick);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('click', this.onTrackClick, true);
      this.track?.removeEventListener('dragstart', this.onDragStart);
      this.endDrag();
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      this.clearTarget();
    }

    // One measurement per frame. While a dot-triggered smooth scroll is in
    // flight, hold the tapped dot instead of walking through the ones it passes.
    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateArrows();
        const index = this.currentIndex();
        if (this.targetIndex !== null) {
          if (index !== this.targetIndex) return;
          this.clearTarget();
        }
        this.setActive(index);
      });
    }

    clearTarget() {
      if (this.targetTimer) {
        clearTimeout(this.targetTimer);
        this.targetTimer = null;
      }
      this.targetIndex = null;
    }

    atEnd() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      return scrollLeft + clientWidth >= scrollWidth - END_TOLERANCE;
    }

    currentIndex() {
      // The last cards cannot snap to the start, so the end of the row is the last dot.
      if (this.atEnd()) return this.track.children.length - 1;
      return this.startIndex();
    }

    // The card nearest the start of the row; arrows step from here, so "previous"
    // from the end of the row moves back one card rather than to the last dot's card.
    startIndex() {
      const cards = this.track.children;
      const { scrollLeft } = this.track;
      const origin = cards[0].offsetLeft;
      let index = 0;
      let closest = Infinity;
      [...cards].forEach((card, i) => {
        const distance = Math.abs(card.offsetLeft - origin - scrollLeft);
        if (distance < closest) {
          closest = distance;
          index = i;
        }
      });
      return index;
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle(ACTIVE_CLASS, active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    updateArrows() {
      if (!this.prev || !this.next) return;
      this.prev.disabled = this.track.scrollLeft <= END_TOLERANCE;
      this.next.disabled = this.atEnd();
    }

    onDotClick(event) {
      this.scrollToIndex(Number(event.currentTarget.dataset.tdSnapDot));
    }

    onPrevClick() {
      this.scrollToIndex(this.startIndex() - 1);
    }

    onNextClick() {
      this.scrollToIndex(this.startIndex() + 1);
    }

    // Mouse only, and only when the row actually overflows (the desktop carousel).
    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.track.scrollWidth - this.track.clientWidth <= END_TOLERANCE) return;
      this.drag = { x: event.clientX, left: this.track.scrollLeft, moved: false };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.drag) return;
      const distance = event.clientX - this.drag.x;
      if (!this.drag.moved) {
        if (Math.abs(distance) < DRAG_THRESHOLD) return;
        this.drag.moved = true;
        this.clearTarget();
        // Snapping off while dragging, or the row fights the pointer.
        this.classList.add(DRAGGING_CLASS);
      }
      this.track.scrollLeft = this.drag.left - distance;
    }

    onPointerUp() {
      const moved = this.drag?.moved;
      this.endDrag();
      if (!moved) return;
      // The click that follows this pointerup belongs to the drag, not the card.
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      });
      this.scrollToIndex(this.startIndex());
    }

    endDrag() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.classList.remove(DRAGGING_CLASS);
      this.drag = null;
    }

    onTrackClick(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    // Stops the browser dragging a card's link or image instead of the row.
    onDragStart(event) {
      if (this.track.scrollWidth - this.track.clientWidth > END_TOLERANCE) event.preventDefault();
    }

    scrollToIndex(index) {
      const cards = this.track.children;
      const card = cards[index];
      if (!card) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.clearTarget();
      if (!reduceMotion) {
        this.targetIndex = index;
        // A swipe can interrupt the smooth scroll before it lands; release the hold.
        this.targetTimer = setTimeout(() => {
          this.clearTarget();
          this.onScroll();
        }, TARGET_HOLD);
      }
      this.track.scrollTo({
        left: card.offsetLeft - cards[0].offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
      this.setActive(index);
    }
  }

  customElements.define('td-snap-dots', TdSnapDots);
}
